import { useMemo } from 'react';
import { ActivityData, ClassroomStatus } from '../../../../interfaces/activity';
import {
    hasExistingClassroom,
    isClassroomCreating,
} from './classroomEligibilityRules';

export type ClassroomButtonState =
    | {
          status: 'ready';
          text: 'Create Classroom';
          isCreating: false;
          hasExistingClassroom: false;
      }
    | {
          status: 'creating';
          text: 'Creating Classroom';
          isCreating: true;
          hasExistingClassroom: boolean;
      }
    | {
          status: 'completed';
          text: 'Classroom Created';
          isCreating: false;
          hasExistingClassroom: true;
      };

/**
 * Custom hook to manage button state based on activity status and submission state
 *
 * @param activity - The activity data
 * @param submitting - Whether an API call is in progress
 * @returns Button state with text, loading, and disabled states
 */
export const useButtonState = (
    activity: ActivityData,
    submitting: boolean,
): ClassroomButtonState => {
    return useMemo(() => {
        const classroomStatus = activity?.classrooms_status;
        const hasExisting = hasExistingClassroom(classroomStatus);
        const isCreating = submitting || isClassroomCreating(classroomStatus);

        // Determine button state based on current conditions
        if (isCreating) {
            return {
                status: 'creating',
                text: 'Creating Classroom',
                isCreating: true,
                hasExistingClassroom: hasExisting,
            };
        }

        if (hasExisting) {
            return {
                status: 'completed',
                text: 'Classroom Created',
                isCreating: false,
                hasExistingClassroom: true,
            };
        }

        return {
            status: 'ready',
            text: 'Create Classroom',
            isCreating: false,
            hasExistingClassroom: false,
        };
    }, [activity?.classrooms_status, submitting]);
};
